import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import * as api from "./api";
import { toast } from "@/components/ui/toast";

/*
 * useGetProjects Query
 *
 * This query is used to get all projects for an account and can be filtered.
 *
 * Query Key: ["projects", params]
 */
export const useGetProjects = (params: api.GetProjectsReq) => {
  return useQuery({
    queryKey: ["projects", params],
    queryFn: () => api.getProjects(params),
  });
};

/*
 * useCreateProject Mutation
 *
 * This mutation is used to create a new project for an account.
 *
 * Query Key: ["projects"]
 */
export const useCreateProject = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: api.createProject,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      toast({
        title: "Project successfully created",
        type: "success",
      });
    },
    onError: () => {
      toast({
        title: "Failed to create project",
        type: "error",
      });
    },
  });
};

/*
 * useGetProject Query
 *
 * This query is used to get a specific project.
 *
 * Query Key: ["projects", projectId]
 */
export const useGetProject = (projectId: string) => {
  return useQuery({
    queryKey: ["projects", projectId],
    queryFn: () => api.getProject(projectId),
    select: (data) => data.project,
  });
};

/*
 * useUpdateProject Mutation
 *
 * This mutation is used to update a specific project.
 *
 * Query Key: ["projects"]
 */
export const useUpdateProject = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: api.updateProject,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      toast({
        title: "Project successfully updated",
        type: "success",
      });
    },
    onError: () => {
      toast({
        title: "Failed to update project",
        type: "error",
      });
    },
  });
};

/*
 * useArchiveProject Mutation
 *
 * It's the same as useUpdateProject, but with a different onSuccess message.
 */
export const useArchiveProject = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: api.updateProject,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      toast({
        title: vars.values.isArchived ? "Project successfully archived" : "Project successfully unarchived",
        type: "success",
      });
    },
    onError: (_, vars) => {
      toast({
        title: vars.values.isArchived ? "Failed to archived project" : "Failed to unarchived project",
        type: "error",
      });
    },
  });
};

/*
 * useDeleteProject Mutation
 *
 * This mutation is used to delete a specific project.
 *
 * Query Key: ["projects"]
 */
export const useDeleteProject = () => {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: api.deleteProject,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["projects"] });
      toast({
        title: "Project successfully deleted",
        type: "success",
      });
    },
    onError: () => {
      toast({
        title: "Failed to delete project",
        type: "error",
      });
    },
  });
};
